import { Helmet } from "react-helmet-async";
import React from "react";
import { Heart, Target, Users, Trophy } from "lucide-react";
import { useSiteImage } from "../context/SiteImagesContext";

const VALUES = [
  { icon: Heart, title: "Passione", text: "Il calcio e lo sport vissuti con il cuore, da oltre 20 anni." },
  { icon: Target, title: "Disciplina", text: "Allenamenti seri, programmi strutturati, obiettivi chiari." },
  { icon: Users, title: "Community", text: "Una famiglia che cresce con il territorio della Val di Susa." },
  { icon: Trophy, title: "Risultati", text: "Trofei, promozioni e — soprattutto — sportivi formati." },
];

export default function ChiSiamo() {
  const heroImg = useSiteImage("chisiamo_hero", "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1600&q=80");
  const mainImg = useSiteImage("chisiamo_main", "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=900&q=80");
  return (
    <div data-testid="chi-siamo-page">
      <Helmet>
  <title>Chi siamo – ASD Rubiana Calcio</title>
  <meta name="description" content="La storia e i valori dell'ASD Rubiana Calcio, società sportiva fondata nel 2003 nel cuore della Val di Susa. Calcio e palestra per tutta la comunità." />
  <meta property="og:title" content="Chi siamo – ASD Rubiana Calcio" />
  <meta property="og:description" content="La storia dell'ASD Rubiana Calcio, società sportiva dal 2003 in Val di Susa." />
  <meta property="og:url" content="https://rubianacalcio.it/chi-siamo" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://rubianacalcio.it/logo512.png" />
</Helmet>
      <section className="relative border-b border-zinc-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 to-zinc-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-overline text-yellow-400 mb-4">// Storia & valori</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter uppercase leading-none">Chi siamo</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-6">
          <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Una società del territorio.</h2>
          <p className="text-zinc-400 leading-relaxed">
            ASD Rubiana Calcio nasce nel 2003 dall'idea di un gruppo di amici che volevano portare
            il calcio dilettantistico nel cuore della Val di Susa. Da allora abbiamo costruito 
            una prima squadra che milita in Terza Categoria e una palestra che
            accoglie ogni settimana persone di tutte le età.
          </p>
          <p className="text-zinc-400 leading-relaxed">
            La nostra missione resta semplice: usare lo sport come strumento educativo e di
            aggregazione. Lavoriamo con allenatori certificati, una struttura sicura e una
            governance trasparente, sostenuti da sponsor locali che credono nel progetto.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4">
            <div><div className="font-display font-black text-4xl text-yellow-400">2003</div><div className="text-xs text-zinc-500 mt-1">Anno di fondazione</div></div>
            <div><div className="font-display font-black text-4xl text-yellow-400">130+</div><div className="text-xs text-zinc-500 mt-1">Tesserati attivi</div></div>
          </div>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="rounded-3xl overflow-hidden">
            <img src={mainImg} alt="Stadio" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-zinc-950 p-6 rounded-2xl max-w-xs hidden md:block">
            <div className="font-display font-black text-3xl leading-none">Forza<br />Rubiana</div>
            <div className="text-xs font-bold uppercase tracking-wider mt-2">Dal 2003</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight mb-10">I nostri <span className="text-yellow-400">valori</span></h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-6 hover:border-yellow-400/40 transition-colors">
              <v.icon className="h-7 w-7 text-yellow-400 mb-4" />
              <h3 className="font-display font-bold text-lg mb-2">{v.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
